import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useTheme } from 'react-jss';
import { makePrioStyles } from '../../../../theme/utils';
import { PrioTheme } from '../../../../theme/types';
import { InformationBox, Modal } from '@prio365/prio365-react-library';
import Flex from '../../../../components/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { ContactId, ProjectId } from '../../../../models/Types';
import useContactsContext from '../../../contacts/hooks/useContactsProvider';
import { useQuery } from '@tanstack/react-query';
import { apiFetchSearch } from '../../../dashboard/api';
import { AbsenceProposalSearchResult } from '../../../../models/AbsenceProposal';
import { useDispatch } from 'react-redux';
import { Message } from '../../../../models/Message';
import ContactPickerPrioCL from '../../../contacts/components/ContactPickerPrioCL';
import { apiAssignMessages } from '../../api';
import { updateMessages } from '../../actions/actionControllers/messageActionController';
import { notification } from 'antd';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  contactPickerAssignmentModal: {
    width: '100%',
  },
  unassignMessageButton: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  infoBoxWrapper: {
    '& .prio-information-box': {
      backgroundColor: 'transparent!important',
      padding: `0px`,
      color: theme.colors.base.yellow.default,
    },
  },
  noSelectionLabel: {
    marginTop: theme.spacing.small,
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.muted,
  },
}));

export const generateSearchString = () => {
  const today = moment().format('YYYY-MM-DD');

  return `From le '${today}' & To ge '${today}'`;
};

interface AssignMessageModalProps {
  className?: string;
  projectId: ProjectId;
  assignMessageModalVisible: boolean;
  setAssignMessageModalVisible: (visible: boolean) => void;
  selectedMessages?: Message[];
  onFinish?: (value: string) => void;
}

export const AssignMessageModal: React.FC<AssignMessageModalProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const {
    className,
    projectId,
    assignMessageModalVisible,
    setAssignMessageModalVisible,
    selectedMessages = [],
    onFinish,
  } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getContactById } = useContactsContext();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [hasAssigneeSelectionChanged, setHasAssigneeSelectionChanged] =
    useState(false);
  const [modalContactId, setModalContactId] = useState<ContactId>(null);
  const [loading, setLoading] = useState(false);

  const modalContact = getContactById(modalContactId);

  const searchString = generateSearchString();

  const { data: _publicAbsenceProposals } = useQuery({
    queryKey: ['publicAbsenceProposals'],
    queryFn: () =>
      apiFetchSearch(
        'publicAbsenceProposals',
        encodeURIComponent(searchString),
        1000,
        false
      ),
    staleTime: 1000 * 60 * 15, // 15 min
  });

  const publicAbsenceProposals = useMemo(() => {
    if (_publicAbsenceProposals?.data) {
      return _publicAbsenceProposals.data
        .items as AbsenceProposalSearchResult[];
    }
    return [];
  }, [_publicAbsenceProposals]);

  const relevantAbsenceProposals = useMemo(() => {
    if (modalContact) {
      const absenceProposals = publicAbsenceProposals.filter(
        (searchResultItem) =>
          searchResultItem.data.applicantId === modalContact.contactId
      );
      return absenceProposals;
    }
    return [];
  }, [modalContact, publicAbsenceProposals]);

  const isAssignedToContactAbsent = useMemo(
    () => relevantAbsenceProposals.length > 0,
    [relevantAbsenceProposals]
  );

  const messageIds = useMemo(() => {
    return selectedMessages?.map((m) => m.id) || [];
  }, [selectedMessages]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleAssignMessageModalOk = async (assigneeId: ContactId) => {
    setLoading(true);
    const { result } = await apiAssignMessages(
      projectId,
      messageIds,
      assigneeId
    );
    setModalContactId(assigneeId);
    setAssignMessageModalVisible(false);

    if (result.status >= 200 && result.status < 300) {
      const updates = messageIds.map((id) => {
        const message = selectedMessages.find((m) => m.id === id);
        return {
          messageId: id,
          messageUpdate: {
            customSingleValueExtendedProperties: {
              ...message?.customSingleValueExtendedProperties,
              messageAssignmentId: assigneeId || null,
            },
          },
        };
      });

      dispatch(updateMessages(projectId, messageIds, updates));
      setLoading(false);
      setHasAssigneeSelectionChanged(true);
      onFinish && onFinish(assigneeId);
    } else {
      notification.open({
        message: t('common:error'),
        description: t(
          `mail:errorMessages.messages.${assigneeId ? 'messageAssignmentError' : 'messageUnassignmentError'}`
        ),
      });
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const firstMessageAssignmentId =
      selectedMessages[0]?.customSingleValueExtendedProperties
        ?.messageAssignmentId;
    const allSame = selectedMessages.every(
      (m) =>
        m.customSingleValueExtendedProperties?.messageAssignmentId ===
          firstMessageAssignmentId ||
        m.customSingleValueExtendedProperties?.messageAssignmentId === null
    );

    if (selectedMessages.length === 1) {
      setModalContactId(firstMessageAssignmentId);
    }
    if (selectedMessages.length > 1) {
      setModalContactId(
        allSame && firstMessageAssignmentId ? firstMessageAssignmentId : null
      );
    }
  }, [selectedMessages]);

  //#endregion

  return (
    <Modal
      className={classNames(classes.root, className)}
      title={t('mail:messageDisplay.assignMessage')}
      visible={assignMessageModalVisible}
      onOk={() => handleAssignMessageModalOk(modalContactId)}
      onClose={() => setAssignMessageModalVisible(false)}
      cancelText={t('mail:modals.assignMessage.cancelText')}
      okText={t('mail:modals.assignMessage.okText')}
      okButtonProps={{ loading: loading }}
      destroyOnClose
    >
      <Flex.Row childrenGap={theme.old.spacing.unit(1)} alignItems="center">
        <ContactPickerPrioCL
          value={modalContactId}
          projectId={projectId}
          onlyInternalProject
          disabled={loading}
          multiple={false}
          className={classes.contactPickerAssignmentModal}
          onChange={(value) => {
            let contactId: ContactId = null;
            Array.isArray(value) ? (contactId = value[0]) : (contactId = value);
            setModalContactId(contactId);
            setHasAssigneeSelectionChanged(true);
          }}
          hideShowMoreButton
          size="default"
        ></ContactPickerPrioCL>
        <FontAwesomeIcon
          icon={['fal', 'xmark']}
          onClick={() => {
            setModalContactId(null);
            setHasAssigneeSelectionChanged(true);
          }}
          className={classes.unassignMessageButton}
        ></FontAwesomeIcon>
      </Flex.Row>
      {modalContact && isAssignedToContactAbsent && (
        <div className={classes.infoBoxWrapper}>
          <InformationBox
            type="warning"
            description={t('mail:messageDisplay.assignMessageWarning', {
              firstName: modalContact.firstName,
              lastName: modalContact.lastName,
              absentTo: moment(relevantAbsenceProposals[0].data.to).format(
                'dd, DD. MMM'
              ),
            })}
            marginBottom="none"
            marginTop="small"
          ></InformationBox>
        </div>
      )}
      {!modalContact && hasAssigneeSelectionChanged && (
        <Flex.Row
          className={classes.noSelectionLabel}
          childrenGap={theme.spacing.regular}
          alignItems="center"
        >
          <FontAwesomeIcon
            icon={['fal', 'info-circle']}
            style={{ margin: 'auto 0' }}
          />
          <span>{t('mail:navigationBar.assignModal.info')}</span>
        </Flex.Row>
      )}
    </Modal>
  );
};

export default AssignMessageModal;
